// Single source of truth for ScopeWorth pricing on the website. Imported by
// the public pricing page (Pages.jsx → PricingPage), the home FAQ
// (Sections.jsx), the sample-report funnel (OnboardingApp.jsx → PricingStep)
// and the early-access form (Pages.jsx → EarlyAccessPage).
//
// Plan prices, limits, the trial, credit packs and AI action prices are NOT
// typed here. They come from contracts/pricing-catalog.json — the exact bytes
// the App generates from its server-owned billing catalog, pinned to an App
// commit and digest-verified before every build (scripts/verify-pricing-
// catalog.mjs, SCO-508). To change a number, change it in the App and re-pin;
// see docs/pricing-catalog.md.
//
// The concierge pilot is a separate, hands-on service, not a plan. Its price
// is deliberately not part of the product catalog and is unchanged here.
import catalog from '../contracts/pricing-catalog.json' with { type: 'json' };

// --- Concierge pilot (separate service, not a plan; unchanged) -------------
export const PILOT_PRICE = '$1,500 to $3,000';

// --- Product catalog (from the pinned App artifact) ------------------------
export const PRICING_CATALOG = catalog;
export const CURRENT_PLANS = catalog.current.plans;
export const LEGACY_PLANS = catalog.legacy.plans;
export const TRIAL = catalog.trial;
// SCO-659: every paid plan (Starter, Growth, Scale) can start with the trial,
// and starting it needs a card on file at checkout; Free never needs a card.
// Copy no longer names a single trial plan, so nothing here reads
// `TRIAL.plan`. Until the App's artifact lists the trial-eligible plans, the
// paid current plans are the eligible ones.
export const TRIAL_PLAN_IDS = catalog.current.plans
  .filter((plan) => plan.monthlyPriceUsdCents > 0)
  .map((plan) => plan.plan);
// The plan card the grid highlights: a presentation choice, not the trial.
export const FEATURED_PLAN = 'starter';
export const CREDIT_PACKS = catalog.creditPacks.packs;
export const AI_ANALYSIS = catalog.aiAnalysis;

export function planById(id) {
  const plan = CURRENT_PLANS.find((entry) => entry.plan === id);
  if (!plan) throw new Error(`pricing catalog has no current plan ${id}`);
  return plan;
}

const isPortuguese = (locale) => String(locale || '').toLowerCase().startsWith('pt');

/** Whole-dollar USD list price. Prices are USD everywhere; tax is extra. */
export function formatUsd(cents, locale = 'en-GB') {
  const dollars = Math.round(cents / 100);
  return isPortuguese(locale)
    ? `US$ ${dollars.toLocaleString('pt-BR')}`
    : `$${dollars.toLocaleString('en-US')}`;
}

export function formatCount(value, locale = 'en-GB') {
  return Number(value).toLocaleString(isPortuguese(locale) ? 'pt-BR' : 'en-US');
}

/** A cap value for display: a localized number, or the "unlimited" word. */
export function formatCap(value, unlimitedWord, locale = 'en-GB') {
  return value === 'unlimited' ? unlimitedWord : formatCount(value, locale);
}

/** "$99" for a plan id, from the catalog. */
export function planPrice(id, locale) {
  return formatUsd(planById(id).monthlyPriceUsdCents, locale);
}

/** Placeholder values the translated copy interpolates. */
export function pricingVars(locale = 'en-GB') {
  const [smallPack, largePack] = CREDIT_PACKS;
  return {
    pilot: PILOT_PRICE,
    starter: planPrice('starter', locale),
    growth: planPrice('growth', locale),
    scale: planPrice('scale', locale),
    trialDays: TRIAL.days,
    trialCredits: formatCount(TRIAL.includedCredits, locale),
    smallPackCredits: formatCount(smallPack.credits, locale),
    smallPackPrice: formatUsd(smallPack.priceUsdCents, locale),
    largePackCredits: formatCount(largePack.credits, locale),
    largePackPrice: formatUsd(largePack.priceUsdCents, locale),
    bandCredits: AI_ANALYSIS.bands.map((band) => band.creditsPerAction).join('/'),
    combinedCredits: AI_ANALYSIS.bands.map((band) => band.interpretationReviewCredits).join('/'),
    starterProjects: planById('starter').activeProjects,
    growthProjects: planById('growth').activeProjects,
    scaleProjects: planById('scale').activeProjects,
  };
}

/** Fills {placeholders} in translated copy with catalog values. */
export function fillPricing(text, locale, extra = {}) {
  const vars = { ...pricingVars(locale), ...extra };
  return String(text).replace(/\{(\w+)\}/g, (match, key) => (key in vars ? String(vars[key]) : match));
}

// --- Buying-intention question options ---------------------------------------
// Stored verbatim in the lead record (early-access `subscription_fit`, max 80
// characters), so each option carries its own price. Q1 — pilot willingness.
export const PILOT_PRICE_FIT_OPTIONS = [
  'Yes, fair for the value',
  'Maybe, depends on the report',
  'Too high for a pilot',
  "I'd only use a free version",
];

// Q2 — plan fit, built from the pinned catalog so an answer names real prices.
const fitOption = (id) => {
  const plan = planById(id);
  return `${plan.label} ${formatUsd(plan.monthlyPriceUsdCents)}/mo (${plan.activeProjects} active projects)`;
};
export const SUBSCRIPTION_FIT_OPTIONS = [
  fitOption('starter'),
  fitOption('growth'),
  fitOption('scale'),
  'Only at a lower price',
  'We’d need custom/annual',
  'Not sure yet',
];

// --- Pricing-page card actions ------------------------------------------------
// `kind` picks the destination: the App's sign-up (a new organization starts
// on Free and picks Free or a paid plan's card-on-file trial at the end of
// setup), the concierge pilot funnel, or a conversation.
export const PLAN_CTA = {
  free: { kind: 'signup', source: 'pricing-free' },
  starter: { kind: 'signup', source: 'pricing-starter' },
  growth: { kind: 'signup', source: 'pricing-growth' },
  scale: { kind: 'signup', source: 'pricing-scale' },
};
export const PILOT_CTA_HREF = '/contact';
export const CUSTOM_CTA_HREF = '/contact';
